<template>
  <svg
    ref="ag_svg"
    :key="'ag-svg-' + agkey"
    :width="width"
    :height="height"
    class="pa-0 ma-0"
  >
    <!-- hearing loss zones -->
    <g v-if="showHearingLossZones && !showChange">
      <g v-for="(hlz, hlz_index) in hl_zones" :key="`hlz-${hlz_index}`">
        <rect
          :x="margin.left"
          :y="yDimensionScale(hlz.min)"
          :width="width - margin.right - margin.left"
          :height="yDimensionScale(hlz.max) - yDimensionScale(hlz.min)"
          stroke="none"
          :fill="hlz.color"
          opacity="0.5"
        ></rect>
        <text
          :x="margin.left + 5"
          :y="yDimensionScale(hlz.min)"
          :dy="dimensions.scale * 0.8 + 'rem'"
          :style="
            'position: absolute; font-size: ' + dimensions.scale * 0.8 + 'rem;'
          "
          text-anchor="start"
          fill="black"
        >
          {{ hlz.title }}
        </text>
      </g>
    </g>

    <!-- background grid -->
    <g v-if="showGrid">
      <!-- horizontal lines -->
      <line
        v-for="(db, db_index) in yDimensionScale.ticks()"
        :key="`bgh-${db_index}`"
        :x1="margin.left"
        :y1="yDimensionScale(db)"
        :x2="width - margin.right"
        :y2="yDimensionScale(db)"
        stroke="#000000"
        stroke-width="1"
      ></line>

      <!-- major vertical lines -->
      <line
        v-for="(hz, hz_index) in hzLines"
        :key="`bgv-${hz_index}`"
        :x1="xDimensionScale(hz)"
        :y1="margin.top"
        :x2="xDimensionScale(hz)"
        :y2="height - margin.bottom"
        stroke="#000000"
        stroke-width="0.5"
      ></line>

      <!-- minor vertical lines -->
      <!-- TODO - use geometric not arithmetic mean? -->
      <g v-if="false">
        <line
          v-for="hz in [750, 1500, 3000, 6000]"
          :key="`bgvm-${hz}`"
          :x1="xDimensionScale(hz)"
          :y1="margin.top"
          :x2="xDimensionScale(hz)"
          :y2="height - margin.bottom"
          stroke="#d0d0d0"
          stroke-width="1"
          stroke-opacity="0.5"
          stroke-dasharray="2,2"
        ></line>
      </g>
    </g>

    <!-- axes and labels-->
    <g
      v-xaxis="xAxisB"
      :transform="`translate(0,${height - margin.bottom})`"
    ></g>
    <text
      v-if="showAxisLabels"
      :x="xDimensionScale(showHighFreqs ? 13000 : 1000)"
      :y="0"
      :dy="dimensions.scale + 'rem'"
      :style="'font-size: ' + dimensions.scale + 'rem;'"
      text-anchor="middle"
      fill="black"
    >
      {{ xAxisTitle }}
    </text>
    <g v-xaxis="yAxisL" :transform="`translate(${margin.left},0)`"></g>
    <g
      v-if="showAxisLabels"
      :transform="'translate(0,' + yDimensionScale(showChange ? 0 : 60) + ')'"
    >
      <text
        :x="0"
        :y="0"
        :dy="dimensions.scale + 'rem'"
        :style="'font-size: ' + dimensions.scale + 'rem;'"
        text-anchor="middle"
        fill="black"
        transform="rotate(-90)"
      >
        {{ yAxisTitle }}
      </text>
    </g>

    <g v-xaxis="xAxisT" :transform="`translate(0,${margin.top})`"></g>
    <g v-xaxis="yAxisR" :transform="`translate(${width - margin.right},0)`"></g>

    <!-- highlighted frequencies -->
    <g v-if="showSignificantFreqs">
      <text
        v-for="(hf, hfi) in Object.keys(highlightFreqs)"
        :key="'hfr' + hfi"
        :x="xDimensionScale(hf)"
        :y="0"
        :dy="dimensions.scale * 2.5 + 'rem'"
        :style="'font-size: ' + dimensions.scale * 1.8 + 'rem;'"
        text-anchor="middle"
        fill="red"
      >
        *
      </text>
    </g>

    <!-- connecting lines - air conduction only -->
    <g
      v-for="(ag, ag_index) in audiograms.filter(
        g =>
          showConnectingLines &&
          ((g.ear == 'L' && g.type == 'air' && showLeftAir) ||
            (g.ear == 'R' && g.type == 'air' && showRightAir) ||
            (g.ear == 'A' && g.type == 'air' && showAffectedAir) ||
            (g.ear == 'U' && g.type == 'air' && showUnaffectedAir) ||
            (g.ear == 'C' && g.type == 'air' && showContralateralAir) ||
            (g.ear == 'I' && g.type == 'air' && showIpsilateralAir))
      )"
      :key="`acl-${ag_index}`"
    >
      <line
        v-for="(c, c_index) in ag.coords.filter(c => c.last_x)"
        :key="`acl-${ag_index}-l${c_index}`"
        :x1="c.last_x"
        :y1="showMedian ? c.last_median_y : c.last_mean_y"
        :x2="c.hz_x"
        :y2="meanOrMedianDbY(c)"
        fill="none"
        :stroke="ag.color"
        :stroke-width="2.5 * dimensions.scale"
        opacity="0.5"
      ></line>
    </g>
    <!-- error bars -->
    <g
      v-for="(ag, ag_index) in audiograms.filter(
        g =>
          showErrorBars &&
          g.coords.length > 0 &&
          'n_values' in g.coords[0] &&
          ((g.ear == 'L' && g.type == 'air' && showLeftAir) ||
            (g.ear == 'R' && g.type == 'air' && showRightAir) ||
            (g.ear == 'A' && g.type == 'air' && showAffectedAir) ||
            (g.ear == 'U' && g.type == 'air' && showUnaffectedAir) ||
            (g.ear == 'C' && g.type == 'air' && showContralateralAir) ||
            (g.ear == 'I' && g.type == 'air' && showIpsilateralAir) ||
            (g.ear == 'L' && g.type == 'bone' && showLeftBone) ||
            (g.ear == 'R' && g.type == 'bone' && showRightBone) ||
            (g.ear == 'A' && g.type == 'bone' && showAffectedBone) ||
            (g.ear == 'U' && g.type == 'bone' && showUnaffectedBone) ||
            (g.ear == 'C' && g.type == 'bone' && showContralateralBone) ||
            (g.ear == 'I' && g.type == 'bone' && showIpsilateralBone))
      )"
      :key="`q13-${ag_index}`"
    >
      <!-- endcap lines -->
      <line
        v-for="(c, c_index) in ag.coords.filter(c => c.n_values > 1)"
        :key="`el1-${c_index}`"
        :x1="c.hz_x - 6"
        :y1="showMedian ? c.q1_db_y : c.plus_1sd_db_y"
        :x2="c.hz_x + 6"
        :y2="showMedian ? c.q1_db_y : c.plus_1sd_db_y"
        :stroke="ag.color"
        stroke-width="1.5"
        opacity="1"
      ></line>
      <line
        v-for="(c, c_index) in ag.coords.filter(c => c.n_values > 1)"
        :key="`el2-${c_index}`"
        :x1="c.hz_x - 6"
        :y1="showMedian ? c.q3_db_y : c.minus_1sd_db_y"
        :x2="c.hz_x + 6"
        :y2="showMedian ? c.q3_db_y : c.minus_1sd_db_y"
        :stroke="ag.color"
        stroke-width="1.5"
        opacity="1"
      ></line>
      <!-- center lines -->
      <line
        v-for="(c, c_index) in ag.coords.filter(c => c.n_values > 1)"
        :key="`cl1-${c_index}`"
        :x1="c.hz_x"
        :y1="showMedian ? c.median_db_y : c.mean_db_y"
        :x2="c.hz_x"
        :y2="showMedian ? c.q1_db_y : c.plus_1sd_db_y"
        :stroke="ag.color"
        stroke-width="1.5"
        opacity="1"
      ></line>
      <line
        v-for="(c, c_index) in ag.coords.filter(c => c.n_values > 1)"
        :key="`cl2-${c_index}`"
        :x1="c.hz_x"
        :y1="showMedian ? c.median_db_y : c.mean_db_y"
        :x2="c.hz_x"
        :y2="showMedian ? c.q3_db_y : c.minus_1sd_db_y"
        :stroke="ag.color"
        stroke-width="1.5"
        opacity="1"
      ></line>
    </g>

    <!-- boxplots -->
    <g
      v-for="(ag, ag_index) in audiograms.filter(
        g =>
          showBoxplots &&
          g.coords.length > 0 &&
          'n_values' in g.coords[0] &&
          ((g.ear == 'L' && g.type == 'air' && showLeftAir) ||
            (g.ear == 'R' && g.type == 'air' && showRightAir) ||
            (g.ear == 'A' && g.type == 'air' && showAffectedAir) ||
            (g.ear == 'U' && g.type == 'air' && showUnaffectedAir) ||
            (g.ear == 'C' && g.type == 'air' && showContralateralAir) ||
            (g.ear == 'I' && g.type == 'air' && showIpsilateralAir) ||
            (g.ear == 'L' && g.type == 'bone' && showLeftBone) ||
            (g.ear == 'R' && g.type == 'bone' && showRightBone) ||
            (g.ear == 'A' && g.type == 'bone' && showAffectedBone) ||
            (g.ear == 'U' && g.type == 'bone' && showUnaffectedBone) ||
            (g.ear == 'C' && g.type == 'bone' && showContralateralBone) ||
            (g.ear == 'I' && g.type == 'bone' && showIpsilateralBone))
      )"
      :key="`bp-${ag_index}`"
    >
      <!-- endcap lines -->
      <line
        v-for="(c, c_index) in ag.coords.filter(c => c.n_values > 1)"
        :key="`el1-${c_index}`"
        :x1="c.hz_x - 5 * dimensions.scale"
        :y1="c.min_db_y"
        :x2="c.hz_x + 5 * dimensions.scale"
        :y2="c.min_db_y"
        :stroke="ag.color"
        stroke-width="1"
        opacity="0.8"
      ></line>
      <line
        v-for="(c, c_index) in ag.coords.filter(c => c.n_values > 1)"
        :key="`el2-${c_index}`"
        :x1="c.hz_x - 5 * dimensions.scale"
        :y1="c.max_db_y"
        :x2="c.hz_x + 5 * dimensions.scale"
        :y2="c.max_db_y"
        :stroke="ag.color"
        stroke-width="1"
        opacity="0.8"
      ></line>
      <!-- center lines -->
      <line
        v-for="(c, c_index) in ag.coords.filter(c => c.n_values > 1)"
        :key="`cl1-${c_index}`"
        :x1="c.hz_x"
        :y1="c.min_db_y"
        :x2="c.hz_x"
        :y2="c.q1_db_y"
        :stroke="ag.color"
        stroke-width="1"
        opacity="0.8"
      ></line>
      <line
        v-for="(c, c_index) in ag.coords.filter(c => c.n_values > 1)"
        :key="`cl2-${c_index}`"
        :x1="c.hz_x"
        :y1="c.q3_db_y"
        :x2="c.hz_x"
        :y2="c.max_db_y"
        :stroke="ag.color"
        stroke-width="1"
        opacity="0.8"
      ></line>
      <!-- filled box -->
      <rect
        v-for="(c, c_index) in ag.coords.filter(c => c.n_values > 1)"
        :key="`fb-${c_index}`"
        :x="c.hz_x - 4 * dimensions.scale"
        :y="c.q1_db_y"
        :width="8 * dimensions.scale"
        :height="c.q3_db_y - c.q1_db_y"
        :stroke="ag.color"
        :fill="ag.color"
        stroke-width="1"
        opacity="0.4"
      ></rect>
      <!-- median line -->
      <line
        v-for="(c, c_index) in ag.coords.filter(c => c.n_values > 1)"
        :key="`ml-${c_index}`"
        :x1="c.hz_x - 4 * dimensions.scale"
        :y1="c.median_db_y"
        :x2="c.hz_x + 4 * dimensions.scale"
        :y2="c.median_db_y"
        :stroke="ag.color"
        stroke-width="2"
        opacity="0.4"
      ></line>
    </g>

    <!-- unmasked air right/unaffected/ipsilateral ear -->
    <g
      v-for="(ag, ag_index) in audiograms.filter(
        g =>
          (g.ear == 'R' && g.type == 'air' && showRightAir) ||
          (g.ear == 'U' && g.type == 'air' && showUnaffectedAir) ||
          (g.ear == 'I' && g.type == 'air' && showIpsilateralAir)
      )"
      :key="`recg-${ag_index}`"
    >
      <g v-for="(c, c_index) in ag.coords" :key="`recg-${ag_index}-${c_index}`">
        <rect
          class="hoverable"
          :x="c.hz_x - 9 * dimensions.scale"
          :y="meanOrMedianDbY(c) - 9 * dimensions.scale"
          :width="18 * dimensions.scale"
          :height="18 * dimensions.scale"
          fill="white"
          stroke="none"
          opacity="0"
        />
        <!-- right = circle -->
        <circle
          v-if="ag.ear == 'R'"
          :key="`rec-${ag_index}-${c_index}`"
          :r="6 * dimensions.scale"
          :cx="c.hz_x"
          :cy="meanOrMedianDbY(c)"
          fill="white"
          :stroke="ag.color"
          stroke-width="2"
        ></circle>
        <!-- ipsilateral = I, unaffected = U -->
        <rect
          v-if="ag.ear == 'I' || ag.ear == 'U'"
          :key="`rec-${ag_index}-${c_index}`"
          :width="12 * dimensions.scale"
          :height="12 * dimensions.scale"
          :x="c.hz_x - 6 * dimensions.scale"
          :y="meanOrMedianDbY(c) - 6 * dimensions.scale"
          :fill="ag.color"
          :stroke="ag.color"
          stroke-width="2"
        ></rect>
        <text
          v-if="ag.ear == 'I' || ag.ear == 'U'"
          :x="c.hz_x"
          :y="meanOrMedianDbY(c)"
          :style="'font-size: ' + dimensions.aud_font_scale + 'rem;'"
          text-anchor="middle"
          fill="white"
          stroke="white"
          dy="0.36em"
        >
          {{ ag.ear }}
        </text>
        <g class="tooltip2 css">
          <rect
            :x="margin.left + 10"
            :y="height - margin.top - 75"
            :width="width - (margin.left + margin.right + 20)"
            height="3.5em"
            fill="#3f51b5"
            rx="5"
            ry="5"
          />
          <text
            :x="margin.left + 20"
            :y="height - margin.top - 65"
            dy=".8em"
            style="position: absolute;"
            text-anchor="start"
            fill="white"
          >
            {{ c.name }}
          </text>
          <text
            :x="margin.left + 20"
            :y="height - margin.top - 65"
            dy="2em"
            style="position: absolute;"
            text-anchor="start"
            fill="white"
          >
            {{ c.hz }}Hz /
            {{
              meanOrMedian() +
                '(' +
                c.n_values +
                ')=' +
                meanOrMedianDb(c).toFixed(1)
            }}
            dB HL
          </text>
        </g>
      </g>
    </g>

    <!-- unmasked air left/affected/contralateral ear -->
    <g
      v-for="(ag, ag_index) in audiograms.filter(
        g =>
          (g.ear == 'L' && g.type == 'air' && showLeftAir) ||
          (g.ear == 'A' && g.type == 'air' && showAffectedAir) ||
          (g.ear == 'C' && g.type == 'air' && showContralateralAir)
      )"
      :key="`lecg-${ag_index}`"
    >
      <g
        v-for="(c, c_index) in ag.coords"
        :key="`lecgg-${ag_index}-${c_index}`"
        class="left_g"
        :transform="'translate(' + c.hz_x + ',' + meanOrMedianDbY(c) + ')'"
      >
        <rect
          class="hoverable"
          :x="-(9 * dimensions.scale)"
          :y="-(9 * dimensions.scale)"
          :width="18 * dimensions.scale"
          :height="18 * dimensions.scale"
          fill="white"
          stroke="none"
          opacity="0"
        />
        <!-- left ear -->
        <line
          v-if="ag.ear == 'L'"
          :key="`lecg-${ag_index}-l1-${c_index}`"
          :x1="-5 * dimensions.scale"
          :y1="-5 * dimensions.scale"
          :x2="5 * dimensions.scale"
          :y2="5 * dimensions.scale"
          :stroke="ag.color"
          stroke-width="2"
        ></line>
        <line
          v-if="ag.ear == 'L'"
          :key="`lecg-${ag_index}-l2-${c_index}`"
          :x1="-5 * dimensions.scale"
          :y1="5 * dimensions.scale"
          :x2="5 * dimensions.scale"
          :y2="-5 * dimensions.scale"
          :stroke="ag.color"
          stroke-width="2"
        ></line>
        <!-- contralateral = C, affected = A -->
        <rect
          v-if="ag.ear == 'C' || ag.ear == 'A'"
          :key="`rec-${ag_index}-${c_index}`"
          :width="12 * dimensions.scale"
          :height="12 * dimensions.scale"
          :x="-6 * dimensions.scale"
          :y="-6 * dimensions.scale"
          :fill="ag.color"
          :stroke="ag.color"
          stroke-width="2"
        ></rect>
        <text
          v-if="ag.ear === 'C' || ag.ear == 'A'"
          :x="0"
          :y="0"
          :style="'font-size: ' + dimensions.aud_font_scale + 'rem;'"
          text-anchor="middle"
          fill="white"
          stroke="white"
          dy="0.36em"
        >
          {{ ag.ear }}
        </text>
        <g class="tooltip2 css">
          <rect
            :x="-c.hz_x + margin.left + 10"
            :y="height - margin.top - meanOrMedianDbY(c) - 75"
            :width="width - (margin.left + margin.right + 20)"
            height="3.5em"
            fill="#3f51b5"
            rx="5"
            ry="5"
          />
          <text
            :x="-c.hz_x + margin.left + 20"
            :y="height - margin.top - meanOrMedianDbY(c) - 65"
            dy=".8em"
            text-anchor="start"
            fill="white"
          >
            {{ c.name }}
          </text>
          <text
            :x="-c.hz_x + margin.left + 20"
            :y="height - margin.top - meanOrMedianDbY(c) - 65"
            dy="2em"
            text-anchor="start"
            fill="white"
          >
            {{ c.hz }}Hz /
            {{
              meanOrMedian() +
                '(' +
                c.n_values +
                ')=' +
                meanOrMedianDb(c).toFixed(1)
            }}
            dB HL
          </text>
        </g>
      </g>
    </g>

    <!-- unmasked bone [mastoid] left ear: ">" sign -->
    <g
      v-for="(ag, ag_index) in audiograms.filter(
        g =>
          (g.ear == 'L' && g.type == 'bone' && showLeftBone) ||
          (g.ear == 'A' && g.type == 'bone' && showAffectedBone) ||
          (g.ear == 'C' && g.type == 'bone' && showContralateralBone)
      )"
      :key="`leub-${ag_index}`"
    >
      <g
        v-for="(c, c_index) in ag.coords"
        :key="`leub-${ag_index}-${c_index}`"
        class="left_g"
        :transform="'translate(' + c.hz_x + ',' + meanOrMedianDbY(c) + ')'"
      >
        <rect
          class="hoverable"
          :x="-9 * dimensions.scale"
          :y="-9 * dimensions.scale"
          :width="18 * dimensions.scale"
          :height="18 * dimensions.scale"
          fill="white"
          stroke="none"
          opacity="0"
        />
        <!-- left ear -->
        <line
          v-if="ag.ear == 'L'"
          :x1="1 * dimensions.scale"
          :y1="-6 * dimensions.scale"
          :x2="7 * dimensions.scale"
          :y2="0"
          :stroke="ag.color"
          stroke-width="2"
        ></line>
        <line
          v-if="ag.ear == 'L'"
          :x1="7 * dimensions.scale"
          :y1="0"
          :x2="1 * dimensions.scale"
          :y2="6 * dimensions.scale"
          :stroke="ag.color"
          stroke-width="2"
        ></line>
        <!-- contralateral = C, affected = A -->
        <rect
          v-if="ag.ear == 'C' || ag.ear == 'A'"
          :key="`rec-${ag_index}-${c_index}`"
          transform="rotate(45)"
          :width="12 * dimensions.scale"
          :height="12 * dimensions.scale"
          :x="-6 * dimensions.scale"
          :y="-6 * dimensions.scale"
          :fill="ag.color"
          :stroke="ag.color"
          stroke-width="2"
        ></rect>
        <text
          v-if="ag.ear === 'C' || ag.ear == 'A'"
          :x="0"
          :y="0"
          :style="'font-size: ' + dimensions.aud_font_scale + 'rem;'"
          text-anchor="middle"
          fill="white"
          stroke="white"
          dy="0.36em"
        >
          {{ ag.ear }}
        </text>
        <g class="tooltip2 css">
          <rect
            :x="-c.hz_x + margin.left + 10"
            :y="height - margin.top - meanOrMedianDbY(c) - 75"
            :width="width - (margin.left + margin.right + 20)"
            height="3.5em"
            fill="#3f51b5"
            rx="5"
            ry="5"
          />
          <text
            :x="-c.hz_x + margin.left + 20"
            :y="height - margin.top - meanOrMedianDbY(c) - 65"
            dy=".8em"
            text-anchor="start"
            fill="white"
          >
            {{ c.name }}
          </text>
          <text
            :x="-c.hz_x + margin.left + 20"
            :y="height - margin.top - meanOrMedianDbY(c) - 65"
            dy="2em"
            text-anchor="start"
            fill="white"
          >
            {{ c.hz }}Hz /
            {{
              meanOrMedian() +
                '(' +
                c.n_values +
                ')=' +
                meanOrMedianDb(c).toFixed(1)
            }}
            dB HL
          </text>
        </g>
      </g>
    </g>

    <!-- unmasked bone [mastoid] right ear: "<" sign -->
    <g
      v-for="(ag, ag_index) in audiograms.filter(
        g =>
          (g.ear == 'R' && g.type == 'bone' && showRightBone) ||
          (g.ear == 'U' && g.type == 'bone' && showUnaffectedBone) ||
          (g.ear == 'I' && g.type == 'bone' && showIpsilateralBone)
      )"
      :key="`reub-${ag_index}`"
    >
      <g
        v-for="(c, c_index) in ag.coords"
        :key="`reub-${ag_index}-${c_index}`"
        class="left_g"
        :transform="'translate(' + c.hz_x + ',' + meanOrMedianDbY(c) + ')'"
      >
        <rect
          class="hoverable"
          :x="-9 * dimensions.scale"
          :y="-9 * dimensions.scale"
          :width="18 * dimensions.scale"
          :height="18 * dimensions.scale"
          fill="white"
          stroke="none"
          opacity="0"
        />
        <line
          v-if="ag.ear == 'L'"
          :x1="-1 * dimensions.scale"
          :y1="6 * dimensions.scale"
          :x2="-7 * dimensions.scale"
          :y2="0"
          :stroke="ag.color"
          stroke-width="2"
        ></line>
        <line
          v-if="ag.ear == 'L'"
          :x1="-7 * dimensions.scale"
          :y1="0"
          :x2="-1 * dimensions.scale"
          :y2="-6 * dimensions.scale"
          :stroke="ag.color"
          stroke-width="2"
        ></line>
        <!-- ipsilateral = I, unaffected = U -->
        <rect
          v-if="ag.ear == 'I' || ag.ear == 'U'"
          :key="`rec-${ag_index}-${c_index}`"
          transform="rotate(45)"
          :width="12 * dimensions.scale"
          :height="12 * dimensions.scale"
          :x="-6 * dimensions.scale"
          :y="-6 * dimensions.scale"
          :fill="ag.color"
          :stroke="ag.color"
          stroke-width="2"
        ></rect>
        <text
          v-if="ag.ear === 'I' || ag.ear == 'U'"
          :x="0"
          :y="0"
          :style="'font-size: ' + dimensions.aud_font_scale + 'rem;'"
          text-anchor="middle"
          fill="white"
          stroke="white"
          dy="0.36em"
        >
          {{ ag.ear }}
        </text>
        <g class="tooltip2 css">
          <rect
            :x="-c.hz_x + margin.left + 10"
            :y="height - margin.top - meanOrMedianDbY(c) - 75"
            :width="width - (margin.left + margin.right + 20)"
            height="3.5em"
            fill="#3f51b5"
            rx="5"
            ry="5"
          />
          <text
            :x="-c.hz_x + margin.left + 20"
            :y="height - margin.top - meanOrMedianDbY(c) - 65"
            dy=".8em"
            text-anchor="start"
            fill="white"
          >
            {{ c.name }}
          </text>
          <text
            :x="-c.hz_x + margin.left + 20"
            :y="height - margin.top - meanOrMedianDbY(c) - 65"
            dy="2em"
            text-anchor="start"
            fill="white"
          >
            {{ c.hz }}Hz /
            {{
              meanOrMedian() +
                '(' +
                c.n_values +
                ')=' +
                meanOrMedianDb(c).toFixed(1)
            }}
            dB HL
          </text>
        </g>
      </g>
    </g>
  </svg>
</template>

<script>
import { mapState } from 'vuex';
import { state } from '@/store/modules/dataExplorer/types';
import { scaleLinear, scaleLog } from 'd3-scale';
import { select } from 'd3-selection';
import { min, max, ascending, mean, deviation, quantile } from 'd3-array';
import { axisTop, axisBottom, axisLeft, axisRight } from 'd3-axis';
import 'd3-transition';
import { save } from 'd3-save-svg';
import { saveSvgAsPng } from 'save-svg-as-png';

// regular/high frequency threshold: <= EHF_THRESHOLD = regular frequency range
let EHF_THRESHOLD = 8500;

// hearing loss zones
let HEARING_LOSS_ZONES = [
  { min: -10, max: 25, title: 'Normal', color: '#e0e0e0' },
  { min: 25, max: 40, title: 'Mild', color: '#c0c0c0' },
  { min: 40, max: 55, title: 'Moderate', color: '#a0a0a0' },
  { min: 55, max: 70, title: 'Moderately Severe', color: '#808080' },
  { min: 70, max: 90, title: 'Severe', color: '#606060' },
  { min: 90, max: 125, title: 'Profound', color: '#404040' },
];

let HZ_LINES = [125, 250, 500, 1000, 2000, 4000, 8000];
let EHF_HZ_LINES = [10000, 12000, 14000, 16000, 18000, 20000];

let DIMENSIONS = {
  small: {
    low: {
      scale: 0.9,
      aud_font_scale: 0.7,
      width: 400,
      height: 353,
      left: 60,
      hz_lines: HZ_LINES,
      hz_labels: [125, 250, 500, 1000, 2000, 4000, 8000],
    },
    high: {
      scale: 0.9,
      aud_font_scale: 0.7,
      width: 233,
      height: 353,
      left: 20,
      hz_lines: EHF_HZ_LINES,
      hz_labels: [8000, 12000, 16000, 20000],
    },
  },
  medium: {
    low: {
      scale: 1.2,
      aud_font_scale: 1,
      width: 600,
      height: 530,
      left: 60,
      hz_lines: HZ_LINES,
      hz_labels: [125, 250, 500, 1000, 2000, 4000, 8000],
    },
    high: {
      scale: 1.2,
      aud_font_scale: 1,
      width: 350,
      height: 530,
      left: 20,
      hz_lines: EHF_HZ_LINES,
      hz_labels: [10000, 12000, 14000, 16000, 18000, 20000],
    },
  },
  large: {
    low: {
      scale: 1.6,
      aud_font_scale: 1.3,
      width: 900,
      height: 795,
      left: 85,
      hz_lines: HZ_LINES,
      hz_labels: [125, 250, 500, 750, 1000, 1500, 2000, 4000, 8000],
    },
    high: {
      scale: 1.6,
      aud_font_scale: 1.3,
      width: 525,
      height: 795,
      left: 30,
      hz_lines: EHF_HZ_LINES,
      hz_labels: [10000, 12000, 14000, 16000, 18000, 20000],
    },
  },
};

export default {
  components: {},
  directives: {
    xaxis(el, binding) {
      const axisMethod = binding.value;
      select(el)
        .transition()
        .call(axisMethod);
    },
  },
  props: {
    analysis: {
      type: Object,
      required: true,
      default: () => {},
    },
    visibleCohorts: {
      type: Array,
      required: true,
      default: () => [],
    },
    showHighFreqs: {
      type: Boolean,
      required: false,
      default: false,
    },
    // left vs right
    showLeftAir: {
      type: Boolean,
      required: false,
      default: true,
    },
    showRightAir: {
      type: Boolean,
      required: false,
      default: true,
    },
    showLeftBone: {
      type: Boolean,
      required: false,
      default: false,
    },
    showRightBone: {
      type: Boolean,
      required: false,
      default: false,
    },
    showAffectedAir: {
      type: Boolean,
      required: false,
      default: true,
    },
    showUnaffectedAir: {
      type: Boolean,
      required: false,
      default: true,
    },
    showContralateralAir: {
      type: Boolean,
      required: false,
      default: true,
    },
    showIpsilateralAir: {
      type: Boolean,
      required: false,
      default: true,
    },
    showAffectedBone: {
      type: Boolean,
      required: false,
      default: false,
    },
    showUnaffectedBone: {
      type: Boolean,
      required: false,
      default: false,
    },
    showContralateralBone: {
      type: Boolean,
      required: false,
      default: false,
    },
    showIpsilateralBone: {
      type: Boolean,
      required: false,
      default: false,
    },
    showLinkingLines: {
      type: Boolean,
      required: false,
      default: true,
    },
    showBoxplots: {
      type: Boolean,
      required: false,
      default: false,
    },
    showMedian: {
      type: Boolean,
      required: false,
      default: false,
    },
    showErrorBars: {
      type: Boolean,
      required: false,
      default: false,
    },
    jitterGroups: {
      type: Boolean,
      required: false,
      default: false,
    },
    showConnectingLines: {
      type: Boolean,
      required: false,
      default: true,
    },
    showGrid: {
      type: Boolean,
      required: false,
      default: true,
    },
    showHearingLossZones: {
      type: Boolean,
      required: false,
      default: true,
    },
    showSignificantFreqs: {
      type: Boolean,
      required: false,
      default: true,
    },
    exportButtonSize: {
      type: String,
      required: false,
      default: 'small',
    },
    xAxisTitle: {
      type: String,
      required: false,
      default: 'Frequency in Hz',
    },
    yAxisTitle: {
      type: String,
      required: false,
      default: 'Hearing level in dB',
    },
    size: {
      type: String,
      required: false,
      default: 'medium',
    },
    highlightPval: {
      type: Number,
      required: false,
      default: 0.05,
    },
    minDb: {
      type: Number,
      required: false,
      default: -10,
    },
    maxDb: {
      type: Number,
      required: false,
      default: 125,
    },
  },
  data() {
    return {
      agkey: 1,
      audiograms: [],
      userAudiograms: [],
      // parameters that depend on showHighFreqs
      showAxisLabels: !this.showHighFreqs,
      showControls: !this.showHighFreqs,
      hl_zones: HEARING_LOSS_ZONES,
      ehf_threshold: EHF_THRESHOLD,
      min_delta_hz_x: 0,
      dimensions: DIMENSIONS[this.size][this.showHighFreqs ? 'high' : 'low'],
    };
  },
  computed: {
    width() {
      return this.dimensions['width'];
    },
    height() {
      return this.dimensions['height'];
    },
    margin() {
      return {
        top: 50 * this.dimensions['scale'],
        right: 35,
        bottom: 20,
        left: this.dimensions['left'],
      };
    },
    hzLines() {
      return this.dimensions['hz_lines'];
    },
    hzLabels() {
      return this.dimensions['hz_labels'];
    },
    showChange() {
      return (
        this.analysis != null && this.analysis.input.comparisonField == 'change'
      );
    },
    highlightFreqs() {
      let hf = {};
      this.audiograms.forEach(a => {
        if (
          (a.ear == 'L' && a.type == 'air' && this.showLeftAir) ||
          (a.ear == 'R' && a.type == 'air' && this.showRightAir) ||
          (a.ear == 'A' && a.type == 'air' && this.showAffectedAir) ||
          (a.ear == 'U' && a.type == 'air' && this.showUnaffectedAir) ||
          (a.ear == 'C' && a.type == 'air' && this.showContralateralAir) ||
          (a.ear == 'I' && a.type == 'air' && this.showIpsilateralAir) ||
          (a.ear == 'L' && a.type == 'bone' && this.showLeftBone) ||
          (a.ear == 'R' && a.type == 'bone' && this.showRightBone) ||
          (a.ear == 'A' && a.type == 'bone' && this.showAffectedBone) ||
          (a.ear == 'U' && a.type == 'bone' && this.showUnaffectedBone) ||
          (a.ear == 'C' && a.type == 'bone' && this.showContralateralBone) ||
          (a.ear == 'I' && a.type == 'bone' && this.showIpsilateralBone)
        ) {
          if ('highlightFreqs' in a) {
            Object.keys(a.highlightFreqs).forEach(freq => {
              hf[freq] = true;
            });
          }
        }
      });
      return hf;
    },
    ...mapState('dataExplorer', {
      data: state.DATA,
      collection: state.COLLECTION,
    }),

    // --------------------------------------------------
    // y-axis
    // --------------------------------------------------

    // --------------------------------------------------
    // x-axis
    // --------------------------------------------------
    xmin() {
      const tp = this.timepoints;
      return tp[0];
    },
    xmax() {
      const tp = this.timepoints;
      return tp[tp.length - 1];
    },
    xDimensionScale() {
      if (this.showHighFreqs) {
        return scaleLinear()
          .domain([8000, 21000])
          .range([this.margin.left, this.width - this.margin.right]);
      } else {
        return scaleLog()
          .domain([90, 11000])
          .range([this.margin.left, this.width - this.margin.right]);
      }
    },
    yDimensionScale() {
      return scaleLinear()
        .domain(this.showChange ? [-60, 60] : [this.minDb, this.maxDb])
        .range([this.margin.top, this.height - this.margin.bottom]);
    },
    xAxisT() {
      return axisTop(this.xDimensionScale)
        .tickValues(this.hzLabels)
        .tickFormat(hz => hz);
    },
    xAxisB() {
      return axisBottom(this.xDimensionScale)
        .tickValues(this.hzLabels)
        .tickFormat(hz => hz);
    },
    yAxisL() {
      if (this.showChange) {
        return axisLeft(this.yDimensionScale).tickFormat(db =>
          db > 0 ? '+' + db : db
        );
      } else if (this.showHighFreqs) {
        return axisLeft(this.yDimensionScale)
          .ticks(15)
          .tickFormat(() => '');
      } else {
        return axisLeft(this.yDimensionScale)
          .ticks(15)
          .tickFormat(db => db);
      }
    },
    yAxisR() {
      return axisRight(this.yDimensionScale)
        .ticks(15)
        .tickFormat(db => db);
    },
  },
  watch: {
    data() {
      this.updatePlot();
    },
    visibleCohorts() {
      this.updatePlot();
    },
    userAudiograms() {
      this.updatePlot();
    },
    highlightPval() {
      this.updatePlot();
    },
    jitterGroups() {
      this.updatePlot();
    },
    size(ns) {
      let dims = DIMENSIONS[ns];
      let shf = this.showHighFreqs ? 'high' : 'low';
      this.dimensions = dims[shf];
      this.updatePlot();
    },
    analysis: {
      handler() {
        this.updatePlot();
      },
      deep: true,
    },
  },
  mounted() {
    this.updatePlot();
  },
  methods: {
    updatePlot() {
      this.audiograms = this.getCohortAudiograms();
    },
    getCohortAudiograms() {
      let pvals = {};
      if ('pvals' in this.analysis && this.analysis.pvals != null) {
        this.analysis.pvals.forEach(a => {
          pvals[a.label] = a;
        });
      }

      //      console.log(
      //        '90-125 = ' + (this.xDimensionScale(125) - this.xDimensionScale(90))
      //      );
      //      console.log(
      //        '11000-8000 = ' +
      //          (this.xDimensionScale(11000) - this.xDimensionScale(8000))
      //      );
      //      console.log(
      //        '1 octave = ' +
      //          (this.xDimensionScale(2000) - this.xDimensionScale(1000))
      //      );
      //      console.log(
      //        '20 dB = ' + (this.yDimensionScale(100) - this.yDimensionScale(80))
      //      );

      let audiograms = [];
      let cnum = -1;

      this.visibleCohorts.forEach(c => {
        cnum += 1;
        let cohort_audiograms = {
          // air
          A: {
            // left ear
            L: {
              name: c.label,
              cnum: cnum,
              ear: 'L',
              type: 'air',
              color: c.color,
              freqs: {},
              highlightFreqs: {},
              n_measurements: 0,
            },
            // right ear
            R: {
              name: c.label,
              cnum: cnum,
              ear: 'R',
              type: 'air',
              color: c.color,
              freqs: {},
              highlightFreqs: {},
              n_measurements: 0,
            },
            // affected ear
            A: {
              name: c.label,
              cnum: cnum,
              ear: 'A',
              type: 'air',
              color: c.color,
              freqs: {},
              highlightFreqs: {},
              n_measurements: 0,
            },
            // unaffected ear
            U: {
              name: c.label,
              cnum: cnum,
              ear: 'U',
              type: 'air',
              color: c.color,
              freqs: {},
              highlightFreqs: {},
              n_measurements: 0,
            },
            // contralateral ear
            C: {
              name: c.label,
              cnum: cnum,
              ear: 'C',
              type: 'air',
              color: c.color,
              freqs: {},
              highlightFreqs: {},
              n_measurements: 0,
            },
            // ipsilateral ear
            I: {
              name: c.label,
              cnum: cnum,
              ear: 'I',
              type: 'air',
              color: c.color,
              freqs: {},
              highlightFreqs: {},
              n_measurements: 0,
            },
          },
          // bone
          B: {
            // left ear
            L: {
              name: c.label,
              cnum: cnum,
              ear: 'L',
              type: 'bone',
              color: c.color,
              freqs: {},
              highlightFreqs: {},
              n_measurements: 0,
            },
            // right ear
            R: {
              name: c.label,
              cnum: cnum,
              ear: 'R',
              type: 'bone',
              color: c.color,
              freqs: {},
              highlightFreqs: {},
              n_measurements: 0,
            },
            // affected ear
            A: {
              name: c.label,
              cnum: cnum,
              ear: 'A',
              type: 'bone',
              color: c.color,
              freqs: {},
              highlightFreqs: {},
              n_measurements: 0,
            },
            // unaffected ear
            U: {
              name: c.label,
              cnum: cnum,
              ear: 'U',
              type: 'bone',
              color: c.color,
              freqs: {},
              highlightFreqs: {},
              n_measurements: 0,
            },
            // contralateral ear
            C: {
              name: c.label,
              cnum: cnum,
              ear: 'C',
              type: 'bone',
              color: c.color,
              freqs: {},
              highlightFreqs: {},
              n_measurements: 0,
            },
            // ipsilateral ear
            I: {
              name: c.label,
              cnum: cnum,
              ear: 'I',
              type: 'bone',
              color: c.color,
              freqs: {},
              highlightFreqs: {},
              n_measurements: 0,
            },
          },
        };
        let showHigh = this.showHighFreqs;
        let showChange = this.showChange;
        let ehf_threshold = this.ehf_threshold;
        let hpv = this.highlightPval;
        let add_measurement = function(ov_label, ear, type, freq, db) {
          if (
            (showHigh && freq <= ehf_threshold) ||
            (!showHigh && freq > ehf_threshold)
          )
            return;

          // ignore anything less than -5 unless displaying change/delta values
          if (db < -5 && !showChange) return;
          let c_ag = cohort_audiograms[type][ear];
          if (!(freq in c_ag['freqs'])) {
            c_ag['freqs'][freq] = [];
          }
          // check if frequency should be highlighted
          if (!(freq in c_ag['highlightFreqs'])) {
            if (ov_label in pvals && pvals[ov_label].pval <= hpv) {
              c_ag['highlightFreqs'][freq] = true;
            }
          }

          c_ag['freqs'][freq].push(db);
          c_ag['n_measurements'] += 1;
        };

        // analyze variables, mapping outcome variable id to ear + frequency
        let ov_id_to_type_ear_freq = {};
        let ov_id_to_label = {};

        c.output_variables
          .filter(ov => 'observation_ontology' in ov)
          .forEach(ov => {
            ov_id_to_label[ov.observation_ontology.id] =
              ov.observation_ontology.label;

            // AudBase
            let ms = ov.observation_ontology.abbreviation.match(
              /^PureTone_([AB])U([LRAUCI])_(\d+)$/
            );
            if (ms) {
              ov_id_to_type_ear_freq[ov.observation_ontology.id] = {
                type: ms[1],
                ear: ms[2],
                freq: ms[3] * 1.0,
              };
              this.title = 'PureTone_AU(RL)_****';
            }
            // NHANES
            ms = ov.observation_ontology.abbreviation.match(
              /^(A)UXU(\d+)(K)?([12])?([LRAUCI])$/
            );
            // ignore AUXU1K2L, AUXU1K2R (2nd read at 1000 Hz)
            if (ms && ms[4] != '2') {
              let type = ms[1];
              let ear = ms[5];
              let hz = ms[3] == 'K' ? ms[2] * 1000.0 : ms[2] * 1.0;

              ov_id_to_type_ear_freq[ov.observation_ontology.id] = {
                type: type,
                ear: ear,
                freq: hz,
              };
              this.title = 'AUXU*K(RL)';
            }

            // NIDCD
            ms = ov.observation_ontology.abbreviation.match(
              /^([LRAUCI])E_([AB])C_(\d+)$/
            );
            if (ms) {
              let type = ms[2];
              let ear = ms[1];
              let hz = ms[3];

              ov_id_to_type_ear_freq[ov.observation_ontology.id] = {
                type: type,
                ear: ear,
                freq: hz,
              };
              this.title = '(RL)E_(AB)C_*';
            }

            // NIDCD
            ms = ov.observation_ontology.abbreviation.match(
              /^([LRAUCI])E_(\d+)_(air)$/
            );
            if (ms) {
              let ear = ms[1];
              let hz = ms[2];
              let type = 'A'; //ms[3].substring(0,1).toUpperCase();

              // high freqs not yet supported
              if (hz <= 8000) {
                ov_id_to_type_ear_freq[ov.observation_ontology.id] = {
                  type: type,
                  ear: ear,
                  freq: hz,
                };
              }
              this.title = '(LE|RE|AE|UE|CE|IE)_*_(air|bone)';
            }
          });

        // index subjects in cohort
        const subjids = [];
        c.subject_ids.forEach(sid => {
          subjids[sid] = 1;
        });

        var comparisonMeasure = this.analysis.input.comparisonField;
        if (!this.analysis.outcomeVariables[0].is_longitudinal)
          comparisonMeasure = 'value';

        let cohortData = this.data.filter(d => d.subject_id in subjids);
        cohortData.forEach(d => {
          Object.keys(d).forEach(ov_id => {
            if (ov_id in ov_id_to_type_ear_freq) {
              let tef = ov_id_to_type_ear_freq[ov_id];
              let db = d[ov_id][comparisonMeasure];
              let ov_label = ov_id_to_label[ov_id];
              add_measurement(
                ov_label,
                tef['ear'],
                tef['type'],
                tef['freq'],
                db
              );
            }
          });
        });
        ['A', 'B'].forEach(cond => {
          ['L', 'R', 'A', 'U', 'C', 'I'].forEach(ear => {
            if (cohort_audiograms[cond][ear]['n_measurements'] > 0)
              audiograms.push(cohort_audiograms[cond][ear]);
          });
        });
      });
      this.userAudiograms.forEach(ua => {
        audiograms.push(ua);
      });
      // update min_delta_hz_x - min space between frequencies
      this.min_delta_hz_x = null;
      audiograms.forEach(a => {
        this.updateAudiogramCoords(a);
      });
      // jitter x-values
      if (this.jitterGroups) {
        let nc = this.visibleCohorts.length;
        // max left or right offset from center line
        let max_offset = this.min_delta_hz_x / 5;
        let jitter_x = nc <= 1 ? 0 : (max_offset * 2) / (nc - 1);
        audiograms.forEach(a => {
          if ('cnum' in a) {
            a['coords'].forEach(c => {
              c['hz_x'] = c['hz_x'] - max_offset + a['cnum'] * jitter_x;
            });
          }
        });
      }
      return audiograms;
    },
    updateAudiogramCoords(ag) {
      let new_coords = [];
      let sorted_freqs = Object.keys(ag['freqs']).sort((a, b) => a - b);
      let last_x = null;
      let last_mean_y = null;
      let last_median_y = null;
      let min_delta_hz_x = this.min_delta_hz_x;

      sorted_freqs.forEach(f => {
        let hz_x = this.xDimensionScale(f);
        let vals = ag['freqs'][f].sort(ascending);
        let mn = mean(vals);
        let sd = deviation(vals);

        // min/max/mean
        let coords = {
          name: ag['name'],
          n_values: vals.length,
          hz: f,
          min_db: min(vals),
          max_db: max(vals),
          mean_db: mn,
          plus_1sd_db: sd ? mn + sd : null,
          minus_1sd_db: sd ? mn - sd : null,
          hz_x: hz_x,
          last_x: last_x,
          last_mean_y: last_mean_y,
          last_median_y: last_median_y,
        };
        // quartiles
        coords['q1_db'] = quantile(vals, 0.25);
        coords['median_db'] = quantile(vals, 0.5);
        coords['q3_db'] = quantile(vals, 0.75);
        let qks = [
          'min_db',
          'max_db',
          'mean_db',
          'plus_1sd_db',
          'minus_1sd_db',
          'q1_db',
          'median_db',
          'q3_db',
        ];
        qks.forEach(k => {
          coords[k + '_y'] = this.yDimensionScale(coords[k]);
        });
        new_coords.push(coords);
        if (last_x != null) {
          let delta_hz_x = hz_x - last_x;
          if (min_delta_hz_x == null || delta_hz_x < min_delta_hz_x) {
            min_delta_hz_x = delta_hz_x;
          }
        }
        last_x = hz_x;
        last_mean_y = coords['mean_db_y'];
        last_median_y = coords['median_db_y'];
      });
      ag['coords'] = new_coords;
      this.min_delta_hz_x = min_delta_hz_x;
    },
    meanOrMedian() {
      return this.showMedian ? 'median' : 'mean';
    },
    meanOrMedianDb(c) {
      return this.showMedian ? c.median_db : c.mean_db;
    },
    meanOrMedianDbY(c) {
      return this.showMedian ? c.median_db_y : c.mean_db_y;
    },
    addAudiogram(ag) {
      // left/right, affected/unaffected, contralateral/ipsilateral
      let ears = ['L', 'R', 'A', 'U', 'C', 'I'];
      ears.forEach(ear => {
        if (ear in ag) {
          this.userAudiograms.push(ag[ear]);
        }
      });
      this.updatePlot();
    },
    removeAllUserAudiograms() {
      this.userAudiograms = [];
    },
    exportPlot(d) {
      this.exportPlotDialog = false;
      if (d['format'] == 'png') {
        this.downloadPng(d['prefix'] + '.png');
      } else if (d['format'] == 'svg') {
        this.downloadSvg(d['prefix']);
      }
    },
    getDownloadFilename() {
      let filename =
        'podvis_d' +
        this.collection.id +
        '_a' +
        this.analysis.index +
        '_audiograms';
      return filename;
    },
    downloadSvg(filename) {
      // save function will append ".svg"
      let config = { filename: filename };
      let agram = this.$refs.ag_svg;
      this.$nextTick(() => {
        save(agram, config);
        // force teardown of SVG element
        this.agkey += 1;
      });
    },
    downloadPng(filename) {
      let agram = this.$refs.ag_svg;
      let options = { backgroundColor: 'white', excludeUnusedCss: true };
      this.$nextTick(() => {
        saveSvgAsPng(agram, filename, options);
        // force teardown of SVG element
        this.agkey += 1;
      });
    },
  },
};
</script>

<style lang="scss" scoped>
div .container {
  padding: 0px important;
}
rect:hoverable {
  fill: '#ffd0d0';
  stroke: 'none';
  opacity: 1;
}
circle:hover {
  stroke-width: 4;
  r: 10;
}
rect:hover ~ g.tooltip2.css {
  opacity: 1;
}
circle:hover ~ g.tooltip2.css {
  opacity: 1;
}
line:hover ~ g.tooltip2.css {
  opacity: 1;
}
rect.hoverable:hover ~ g.tooltip2.css {
  opacity: 1;
}
rect.hoverable:hover ~ line {
  stroke-width: 4;
}
rect.hoverable ~ rect:hover {
  stroke-width: 4;
}
rect.hoverable ~ text {
  pointer-events: none;
}

.tooltip2 {
  pointer-events: none; /*let mouse events pass through*/
  opacity: 0;
  text-shadow: 1px 1px 0px gray;
}
g.tooltip2 rect {
  fill: '#3f51b5';
}
</style>
